import { useState } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { useDeleteResource, useResources, useSaveResource } from '../../api/referentiel';
import type { Resource, ResourceInput, ResourceStatus, ResourceType } from '../../api/types';
import { ApiError } from '../../api/client';
import { formatDuration, formatTimeOfDay, parseTimeOfDay } from './format';

const STATUS_LABEL: Record<ResourceStatus, string> = {
  AVAILABLE: 'Disponible',
  MAINTENANCE: 'Maintenance',
  OUT_OF_SERVICE: 'Hors service',
};

const STATUS_DOT: Record<ResourceStatus, string> = {
  AVAILABLE: 'bg-green-500',
  MAINTENANCE: 'bg-amber-500',
  OUT_OF_SERVICE: 'bg-red-500',
};

function emptyResource(): ResourceInput {
  return {
    code: '',
    label: '',
    type: 'AUTOCLAVE',
    status: 'AVAILABLE',
    maxTc: 12,
    maxVacuumPorts: 8,
    leadTimeBeforeLoadingMinutes: 120,
    openFromMinutes: null,
    openToMinutes: null,
  };
}

export default function ResourcesPanel() {
  const { data: resources, isLoading, error } = useResources();
  const save = useSaveResource();
  const remove = useDeleteResource();
  const [editing, setEditing] = useState<{ id?: number; data: ResourceInput } | null>(null);

  if (isLoading) return <p className="text-gray-500">Chargement des moyens…</p>;
  if (error) return <p className="text-red-600">Erreur de chargement des moyens chauds.</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Moyens chauds</h2>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-2"
          onClick={() => setEditing({ data: emptyResource() })}
        >
          <Plus size={18} /> Nouveau moyen
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {resources?.map((r) => (
          <ResourceCard key={r.id} resource={r} onEdit={() => setEditing({ id: r.id, data: toInput(r) })} onDelete={() => remove.mutate(r.id)} />
        ))}
      </div>

      {editing && (
        <ResourceModal
          value={editing}
          saving={save.isPending}
          error={save.error}
          onClose={() => {
            save.reset();
            setEditing(null);
          }}
          onSubmit={(data) =>
            save.mutate(
              { id: editing.id, data },
              {
                onSuccess: () => {
                  save.reset();
                  setEditing(null);
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

function toInput(r: Resource): ResourceInput {
  const { id: _id, ...rest } = r;
  void _id;
  return rest;
}

function ResourceCard({ resource, onEdit, onDelete }: { resource: Resource; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="bg-white rounded-lg shadow border border-gray-100">
      <div className="px-4 py-3 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${STATUS_DOT[resource.status]}`} />
          <span className="font-semibold text-gray-800">{resource.label}</span>
          <span className="text-xs text-gray-400">{resource.code}</span>
        </div>
        <div className="flex gap-2">
          <button className="text-blue-600 hover:text-blue-800" onClick={onEdit} aria-label="Modifier">
            <Pencil size={18} />
          </button>
          <button
            className="text-red-600 hover:text-red-800"
            onClick={() => {
              if (confirm(`Supprimer ${resource.label} ?`)) onDelete();
            }}
            aria-label="Supprimer"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      <dl className="p-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Field label="Type" value={resource.type === 'AUTOCLAVE' ? 'Autoclave' : 'Étuve'} />
        <Field label="État" value={STATUS_LABEL[resource.status]} />
        <Field label="Tc max" value={String(resource.maxTc)} />
        <Field label="Voies de vide max" value={String(resource.maxVacuumPorts)} />
        <Field label="Avance avant chargement" value={formatDuration(resource.leadTimeBeforeLoadingMinutes)} />
        <Field
          label="Plage d'ouverture"
          value={
            resource.openFromMinutes == null && resource.openToMinutes == null
              ? 'En continu'
              : `${formatTimeOfDay(resource.openFromMinutes)} – ${formatTimeOfDay(resource.openToMinutes)}`
          }
        />
      </dl>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-gray-900">{value}</dd>
    </div>
  );
}

function ResourceModal({
  value,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  value: { id?: number; data: ResourceInput };
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (data: ResourceInput) => void;
}) {
  const [form, setForm] = useState<ResourceInput>(value.data);
  const set = <K extends keyof ResourceInput>(key: K, v: ResourceInput[K]) => setForm((f) => ({ ...f, [key]: v }));

  return (
    <Modal title={value.id ? 'Modifier le moyen' : 'Nouveau moyen'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Code" value={form.code} onChange={(v) => set('code', v)} required />
          <TextField label="Libellé" value={form.label} onChange={(v) => set('label', v)} required />
          <SelectField label="Type" value={form.type} onChange={(v) => set('type', v as ResourceType)} options={[['AUTOCLAVE', 'Autoclave'], ['ETUVE', 'Étuve']]} />
          <SelectField label="État" value={form.status} onChange={(v) => set('status', v as ResourceStatus)} options={[['AVAILABLE', 'Disponible'], ['MAINTENANCE', 'Maintenance'], ['OUT_OF_SERVICE', 'Hors service']]} />
          <NumberField label="Tc max" value={form.maxTc} onChange={(v) => set('maxTc', v)} min={0} />
          <NumberField label="Voies de vide max" value={form.maxVacuumPorts} onChange={(v) => set('maxVacuumPorts', v)} min={0} />
          <NumberField label="Avance avant chargement (min)" value={form.leadTimeBeforeLoadingMinutes} onChange={(v) => set('leadTimeBeforeLoadingMinutes', v)} min={0} />
          <div />
          <TimeField label="Ouverture" minutes={form.openFromMinutes} onChange={(v) => set('openFromMinutes', v)} />
          <TimeField label="Fermeture" minutes={form.openToMinutes} onChange={(v) => set('openToMinutes', v)} />
        </div>

        <ApiErrorMessage error={error} />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// --- Primitives de formulaire partagees ---

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Fermer">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function TextField({ label, value, onChange, required }: { label: string; value: string; onChange: (v: string) => void; required?: boolean }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input className="mt-1 w-full border rounded px-3 py-2" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </label>
  );
}

export function NumberField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input type="number" className="mt-1 w-full border rounded px-3 py-2" value={value} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

export function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <select className="mt-1 w-full border rounded px-3 py-2 bg-white" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

function TimeField({ label, minutes, onChange }: { label: string; minutes: number | null; onChange: (v: number | null) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input
        type="time"
        className="mt-1 w-full border rounded px-3 py-2"
        value={minutes == null ? '' : formatTimeOfDay(minutes)}
        onChange={(e) => onChange(parseTimeOfDay(e.target.value))}
      />
    </label>
  );
}

export function ApiErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    const details = Array.isArray(error.details) ? (error.details as { message: string }[]) : null;
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
        <p className="font-medium">{error.message}</p>
        {details && (
          <ul className="mt-1 list-disc list-inside">
            {details.map((d, i) => (
              <li key={i}>{d.message}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  return <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">Une erreur est survenue.</div>;
}
